import translate from '../i18n/config';

export const API_PREFIX = '/api/';

export const API_URL = process.env.REACT_APP_API_URL;

const ENTITY = {
  auth: 'auth',
  users: 'user',
  roles: 'role',
  properties: 'property',
  platform: 'platform',
  vendor: 'vendor',
  transactions: 'transaction',
  serviceTypes: 'service-type',
  transactionType: 'transaction-type',
  customers: 'customer',
  reservations: 'reservation',
  invoices: 'invoice',
  payments: 'payment',
  payouts: 'reconciliation-payout',
  account: 'account',
  countries: 'country',
  states: 'state',
  city: 'city',
  genericLookup: 'generic-lookup',
  userPropertyAssignment: 'user-property-assignment',
};

export const PATH = {
  login: 'login',
  logout: 'logout',
  forgotPassword: 'forgot-password',
  resetPassword: 'reset-password',
  search: 'search',
  preview: 'preview',
  complete: 'mark-as-complete',
  sendEmail: 'send-email',
  add: 'add',
  managers: 'manager',
  lookup: 'lookup',
  syncHospitable: 'sync-hospitable',
  changePassword: 'change-password',
  kpis: 'kpi',
  properties: 'properties',
  users: 'users',
  transactions: 'transactions',
  bankAccount: 'bank_account',
  payout: 'payout',
  sync: 'sync'
};

const API_PATHS = {
  login: `${ENTITY.auth}/${PATH.login}`,
  logout: `${ENTITY.auth}/${PATH.logout}`,
  forgotPassword: `${ENTITY.auth}/${PATH.forgotPassword}`,
  resetPassword: `${ENTITY.auth}/${PATH.resetPassword}`,
  listUsers: `${ENTITY.users}/${PATH.search}`,
  listProperties: `${ENTITY.properties}/${PATH.search}`,
  properties: `${ENTITY.properties}`,
  listTransactions: `${ENTITY.transactions}/${PATH.search}`,
  transactions: `${ENTITY.transactions}`,
  users: `${ENTITY.users}`,
  vendors: `${ENTITY.vendor}`,
  customers: `${ENTITY.customers}`,
  listVendors: `${ENTITY.vendor}/${PATH.search}`,
  listCustomers: `${ENTITY.customers}/${PATH.search}`,
  vendorListKpis: `${ENTITY.vendor}/${PATH.kpis}`,
  propertyKpis: `${ENTITY.properties}/${PATH.kpis}`,
  customerKpis: `${ENTITY.customers}/${PATH.kpis}`,
  changePassword: `${ENTITY.users}/${PATH.changePassword}`,
  userListKpis: `${ENTITY.users}/${PATH.kpis}`,
  listRoles: `${ENTITY.roles}/${PATH.search}`,
  lookupUsers: `${ENTITY.users}/${PATH.lookup}`,
  lookupVendors: `${ENTITY.vendor}/${PATH.lookup}`,
  lookupRoles: `${ENTITY.roles}/${PATH.lookup}`,
  lookupCustomers: `${ENTITY.customers}/${PATH.lookup}`,
  lookupProperties: `${ENTITY.properties}/${PATH.lookup}`,
  lookupManagers: `${ENTITY.users}/${PATH.lookup}`,
  serviceTypesLookup: `${ENTITY.serviceTypes}/${PATH.lookup}`,
  lookupCountries: `${ENTITY.countries}/${PATH.lookup}`,
  lookupStates: `${ENTITY.states}/${PATH.lookup}`,
  lookupCities: `${ENTITY.city}/${PATH.lookup}`,
  lookupGeneric: `${ENTITY.genericLookup}/${PATH.lookup}`,
  userPropertyAssignment: `${ENTITY.userPropertyAssignment}`,
  userProperties: `${ENTITY.users}/${PATH.properties}/${PATH.search}`,
  propertyUsers: `${ENTITY.properties}/${PATH.users}/${PATH.search}`,
  customerProperties: `${ENTITY.customers}/${PATH.properties}/${PATH.search}`,
  transactionsKpis: `${ENTITY.transactions}/${PATH.kpis}`,
  lookupTransactionTypes: `${ENTITY.transactionType}/${PATH.lookup}`,
  lookupPlatforms: `${ENTITY.platform}/${PATH.lookup}`,
  listReservations: `${ENTITY.reservations}/${PATH.search}`,
  reservations: `${ENTITY.reservations}`,
  lookupReservations: `${ENTITY.reservations}/${PATH.lookup}`,
  syncReservations: `${ENTITY.reservations}/${PATH.syncHospitable}`,
  reservationPayoutDetails: (id: string) => `${ENTITY.reservations}/payout-details/${id}`,
  listInvoices: `${ENTITY.invoices}/${PATH.search}`,
  invoices: `${ENTITY.invoices}`,
  invoicePreview: (id: string) => `${ENTITY.invoices}/${id}/${PATH.preview}`,
  invoiceComplete: `${ENTITY.invoices}/${PATH.complete}`,
  invoiceSendEmail: (id: string) => `${ENTITY.invoices}/${id}/${PATH.sendEmail}`,
  listPayments: `${ENTITY.payments}/${PATH.search}`,
  payments: `${ENTITY.payments}`,
  paymentKpis: `${ENTITY.payments}/${PATH.kpis}`,
  lookupAccounts: `${ENTITY.account}/${PATH.lookup}`,
  lookupPaymentMethods: `${ENTITY.payments}/${PATH.lookup}`,
  lookupInvoice: `${ENTITY.invoices}/${PATH.lookup}`,
  listPayouts: `${ENTITY.payouts}/${PATH.search}`,
  lookupPayoutBankAccounts: `${ENTITY.payouts}/${PATH.bankAccount}/${PATH.lookup}`,
  lookupPayoutTypes: `${ENTITY.payouts}/${PATH.payout}/${PATH.lookup}`,
  payouts: `${ENTITY.payouts}`,
  syncPayouts: `${PATH.payout}/${PATH.sync}`,
  addPayout: (id: string) => `${ENTITY.payouts}/add-payout/${id}`,
  transactionAttachmentInit: (transactionId: string) => `${ENTITY.transactions}/${transactionId}/attachments/init`,
  transactionAttachmentChunk: (transactionId: string, sessionId: string) => `${ENTITY.transactions}/${transactionId}/attachments/${sessionId}/chunk`,
  transactionAttachmentFinalize: (transactionId: string, sessionId: string) => `${ENTITY.transactions}/${transactionId}/attachments/${sessionId}/finalize`,
  transactionAttachment: (transactionId: string, attachmentId: string) => `${ENTITY.transactions}/${transactionId}/attachments/${attachmentId}`,
  transactionAttachmentDownloadUrl: (transactionId: string, attachmentId: string) => `${ENTITY.transactions}/${transactionId}/attachments/${attachmentId}/download-url`,
};

export const API_ENDPOINTS = {
  login: API_PREFIX + API_PATHS.login,
  logout: API_PREFIX + API_PATHS.logout,
  forgotPassword: API_PREFIX + API_PATHS.forgotPassword,
  resetPassword: API_PREFIX + API_PATHS.resetPassword,
  listUsers: API_PREFIX + API_PATHS.listUsers,
  addUser: API_PREFIX + API_PATHS.users,
  editUser: API_PREFIX + API_PATHS.users,
  changePassword: API_PREFIX + API_PATHS.changePassword,
  getUser: API_PREFIX + API_PATHS.users,
  lookupUsers: API_PREFIX + API_PATHS.lookupUsers,
  lookupVendors: API_PREFIX + API_PATHS.lookupVendors,
  userListKpis: API_PREFIX + API_PATHS.userListKpis,
  lookupManagers: API_PREFIX + API_PATHS.lookupManagers,
  listRoles: API_PREFIX + API_PATHS.listRoles,
  lookupRoles: API_PREFIX + API_PATHS.lookupRoles,
  listProperties: API_PREFIX + API_PATHS.listProperties,
  properties: API_PREFIX + API_PATHS.properties,
  listTransactions: API_PREFIX + API_PATHS.listTransactions,
  transactions: API_PREFIX + API_PATHS.transactions,
  lookupProperties: API_PREFIX + API_PATHS.lookupProperties,
  listVendors: API_PREFIX + API_PATHS.listVendors,
  vendorListKpis: API_PREFIX + API_PATHS.vendorListKpis,
  propertyKpis: API_PREFIX + API_PATHS.propertyKpis,
  vendors: API_PREFIX + API_PATHS.vendors,
  listCustomers: API_PREFIX + API_PATHS.listCustomers,
  lookupCustomers: API_PREFIX + API_PATHS.lookupCustomers,
  customerKpis: API_PREFIX + API_PATHS.customerKpis,
  customers: API_PREFIX + API_PATHS.customers,
  serviceTypesLookup: API_PREFIX + API_PATHS.serviceTypesLookup,
  lookupCountries: API_PREFIX + API_PATHS.lookupCountries,
  lookupStates: API_PREFIX + API_PATHS.lookupStates,
  lookupCities: API_PREFIX + API_PATHS.lookupCities,
  lookupGeneric: API_PREFIX + API_PATHS.lookupGeneric,
  assignRemoveUserProperty: API_PREFIX + API_PATHS.userPropertyAssignment,
  bulkAssignUserProperty: API_PREFIX + API_PATHS.userPropertyAssignment + '/bulk-assign',
  userProperties: API_PREFIX + API_PATHS.userProperties,
  propertyUsers: API_PREFIX + API_PATHS.propertyUsers,
  customerProperties: API_PREFIX + API_PATHS.customerProperties,
  transactionsKpis: API_PREFIX + API_PATHS.transactionsKpis,
  lookupTransactionTypes: API_PREFIX + API_PATHS.lookupTransactionTypes,
  lookupPlatforms: API_PREFIX + API_PATHS.lookupPlatforms,
  listReservations: API_PREFIX + API_PATHS.listReservations,
  reservations: API_PREFIX + API_PATHS.reservations,
  lookupReservations: API_PREFIX + API_PATHS.lookupReservations,
  syncReservations: API_PREFIX + API_PATHS.syncReservations,
  reservationPayoutDetails: (id: string) => API_PREFIX + API_PATHS.reservationPayoutDetails(id),
  listInvoices: API_PREFIX + API_PATHS.listInvoices,
  invoices: API_PREFIX + API_PATHS.invoices,
  invoicePreview: (id: string) => `${API_PREFIX}${API_PATHS.invoicePreview(id)}`,
  invoiceComplete: API_PREFIX + API_PATHS.invoiceComplete,
  invoiceSendEmail: (id: string) => `${API_PREFIX}${API_PATHS.invoiceSendEmail(id)}`,
  lookupInvoice: API_PREFIX + API_PATHS.lookupInvoice,
  listPayments: API_PREFIX + API_PATHS.listPayments,
  payments: API_PREFIX + API_PATHS.payments,
  paymentKpis: API_PREFIX + API_PATHS.paymentKpis,
  lookupAccounts: API_PREFIX + API_PATHS.lookupAccounts,
  lookupPaymentMethods: API_PREFIX + API_PATHS.lookupPaymentMethods,
  listPayouts: API_PREFIX + API_PATHS.listPayouts,
  lookupPayoutBankAccounts: API_PREFIX + API_PATHS.lookupPayoutBankAccounts,
  lookupPayoutTypes: API_PREFIX + API_PATHS.lookupPayoutTypes,
  payouts: API_PREFIX + API_PATHS.payouts,
  syncPayouts: API_PREFIX + API_PATHS.syncPayouts,
  addPayout: (id: string) => `${API_PREFIX}${API_PATHS.addPayout(id)}`,
  transactionAttachmentInit: (transactionId: string) => `${API_PREFIX}${API_PATHS.transactionAttachmentInit(transactionId)}`,
  transactionAttachmentChunk: (transactionId: string, sessionId: string) => `${API_PREFIX}${API_PATHS.transactionAttachmentChunk(transactionId, sessionId)}`,
  transactionAttachmentFinalize: (transactionId: string, sessionId: string) => `${API_PREFIX}${API_PATHS.transactionAttachmentFinalize(transactionId, sessionId)}`,
  transactionAttachment: (transactionId: string, attachmentId: string) => `${API_PREFIX}${API_PATHS.transactionAttachment(transactionId, attachmentId)}`,
  transactionAttachmentDownloadUrl: (transactionId: string, attachmentId: string) => `${API_PREFIX}${API_PATHS.transactionAttachmentDownloadUrl(transactionId, attachmentId)}`,
};

export const STATUS_CODE = {
  unauthorized: 401,
  badRequest: 400,
  unProcessableEntity: 422,
  forbidden: 403,
  notFound: 404,
  internalServerError: 500,
  gatewayTimeout: 504,
  entityTooLarge: 413,
  conflict: 409,
};

export const ERROR_MESSAGES = {
  internalServerError: translate.t('common.errorMessages.internalServerError'),
  unauthorized: translate.t('common.errorMessages.unauthorized'),
  badRequest: translate.t('common.errorMessages.badRequest'),
  apiPathNotFound: translate.t('common.errorMessages.apiPathNotFound'),
  forbidden: translate.t('common.errorMessages.forbidden'),
  gatewayTimeout: translate.t('common.errorMessages.gatewayTimeout'),
};
